import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUniversity, faPlus, faTrash, faStar, faCheckCircle,
  faSpinner, faTimes, faExclamationTriangle, faMagnifyingGlass,
  faChevronDown, faShieldHalved, faPen
} from '@fortawesome/free-solid-svg-icons';
import { userAPI } from '../services/api';

const EMPTY_FORM = { bank_name: '', bank_code: '', account_number: '', account_name: '' };

const BankAccountsManager = ({ onClose }) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [defaultBankId, setDefaultBankId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Bank list + searchable picker
  const [banks, setBanks] = useState([]);
  const [bankSearch, setBankSearch] = useState('');
  const [bankListOpen, setBankListOpen] = useState(false);
  const bankPickerRef = useRef(null);

  // Form + verification state
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [errors, setErrors] = useState({});
  const [addBankError, setAddBankError] = useState(null);

  // Inline toast (success / error feedback inside the modal)
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  useEffect(() => {
    loadBankAccounts();
    loadBanks();
  }, []);

  // Close bank dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bankPickerRef.current && !bankPickerRef.current.contains(e.target)) {
        setBankListOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadBankAccounts = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getBankAccounts();
      const data = response.data || response;
      setBankAccounts(data.bank_accounts || []);
      setDefaultBankId(data.default_bank_id);
    } catch (error) {
      // silent — list stays empty
    } finally {
      setLoading(false);
    }
  };

  const loadBanks = async () => {
    try {
      const response = await userAPI.getBanks();
      const list = response?.data || response || [];
      if (Array.isArray(list) && list.length > 0) {
        setBanks(list.filter(b => b?.name && b?.code));
      }
    } catch (error) {
      // fallback: common Nigerian banks so the picker still works offline
      setBanks([
        { name: 'Access Bank', code: '044' }, { name: 'Guaranty Trust Bank', code: '058' },
        { name: 'Zenith Bank', code: '057' }, { name: 'United Bank for Africa', code: '033' },
        { name: 'First Bank of Nigeria', code: '011' }, { name: 'Wema Bank', code: '035' },
        { name: 'Sterling Bank', code: '232' }, { name: 'Fidelity Bank', code: '070' },
        { name: 'Union Bank of Nigeria', code: '032' }, { name: 'Stanbic IBTC Bank', code: '221' },
        { name: 'Ecobank Nigeria', code: '050' }, { name: 'FCMB', code: '214' },
        { name: 'Keystone Bank', code: '082' }, { name: 'Polaris Bank', code: '076' },
        { name: 'Kuda Microfinance Bank', code: '50211' }, { name: 'OPay', code: '999992' },
        { name: 'PalmPay', code: '999991' }, { name: 'Moniepoint MFB', code: '50515' },
      ]);
    }
  };

  const filteredBanks = useMemo(() => {
    const q = bankSearch.trim().toLowerCase();
    if (!q) return banks.slice(0, 60);
    return banks.filter(b => b.name.toLowerCase().includes(q)).slice(0, 60);
  }, [banks, bankSearch]);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setBankSearch('');
    setVerified(false);
    setVerifyFailed(false);
    setManualEntry(false);
    setErrors({});
    setAddBankError(null);
  };

  const selectBank = (bank) => {
    setFormData(prev => ({ ...prev, bank_name: bank.name, bank_code: bank.code }));
    setBankSearch(bank.name);
    setBankListOpen(false);
    setVerified(false);
    setVerifyFailed(false);
    setManualEntry(false);
    setAddBankError(null);
  };

  const handleAccountNumberChange = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, account_number: digits, account_name: manualEntry ? prev.account_name : '' }));
    setVerified(false);
    setVerifyFailed(false);
    setAddBankError(null);
    setErrors(prev => ({ ...prev, account_number: undefined, general: undefined }));
  };

  // Auto-verify as soon as we have a selected bank + 10-digit account number.
  // Uses a request sequence ref (not effect cleanup) to ignore stale responses —
  // cancelling in cleanup would abort our own request when state changes re-render.
  const verifyReqRef = useRef(0);
  useEffect(() => {
    if (manualEntry) return;
    if (formData.account_number.length !== 10 || !formData.bank_code) {
      verifyReqRef.current += 1; // invalidate any in-flight verification
      setVerifying(false);
      return;
    }

    const reqId = ++verifyReqRef.current;
    setVerifying(true);
    setVerified(false);
    setVerifyFailed(false);

    const timer = setTimeout(async () => {
      try {
        const response = await userAPI.verifyBankAccount(formData.account_number, formData.bank_code);
        if (verifyReqRef.current !== reqId) return; // superseded by newer input
        const accountData = response?.data || {};
        const name = accountData.account_name || accountData.accountName;
        if (name) {
          setFormData(prev => ({ ...prev, account_name: name }));
          setVerified(true);
          setErrors({});
        } else {
          setVerifyFailed(true);
        }
      } catch (error) {
        if (verifyReqRef.current === reqId) setVerifyFailed(true);
      } finally {
        if (verifyReqRef.current === reqId) setVerifying(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.account_number, formData.bank_code, manualEntry]);

  const handleAddBank = async (e) => {
    e.preventDefault();
    setAddBankError(null);

    const newErrors = {};
    if (!formData.bank_code) newErrors.bank = 'Please select your bank';
    if (!/^\d{10}$/.test(formData.account_number)) newErrors.account_number = 'Account number must be 10 digits';
    if (!formData.account_name.trim()) newErrors.account_name = manualEntry ? 'Enter the account name' : 'Account not verified yet';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setProcessing(true);
      await userAPI.addBankAccount({
        bank_name: formData.bank_name,
        account_number: formData.account_number,
        account_name: formData.account_name.trim(),
        bank_code: formData.bank_code,
        is_verified: verified,
      });

      resetForm();
      setShowAddForm(false);
      await loadBankAccounts();
      showToast('success', 'Bank account added successfully');
    } catch (error) {
      const errorMessage = error.message || 'Failed to add bank account';
      const lowerMsg = errorMessage.toLowerCase();

      if (lowerMsg.includes('duplicate') || lowerMsg.includes('already exists') || lowerMsg.includes('already added')) {
        setAddBankError({ type: 'duplicate', message: 'This bank account has already been added to your profile.' });
      } else if (
        lowerMsg.includes('name') || lowerMsg.includes('match') ||
        lowerMsg.includes('mismatch') || lowerMsg.includes('account holder')
      ) {
        let profileName = '';
        try {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          const fn = stored.first_name || stored.firstName || '';
          const ln = stored.last_name || stored.lastName || '';
          if (fn || ln) profileName = `${fn} ${ln}`.trim();
        } catch { /* ignore */ }

        setAddBankError({
          type: 'name_mismatch',
          message: errorMessage,
          bankName: formData.account_name,
          profileName,
        });
      } else {
        setAddBankError({ type: 'generic', message: errorMessage });
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleSetDefault = async (bankId) => {
    try {
      setProcessing(true);
      await userAPI.setDefaultBankAccount(bankId);
      setDefaultBankId(bankId);
      showToast('success', 'Default bank account updated');
    } catch (error) {
      showToast('error', 'Failed to set default bank account');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteBank = async (bankId) => {
    if (!window.confirm('Remove this bank account?')) return;
    try {
      setProcessing(true);
      await userAPI.deleteBankAccount(bankId);
      await loadBankAccounts();
      showToast('success', 'Bank account removed');
    } catch (error) {
      showToast('error', error.message || 'Failed to delete bank account');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bam-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{BAM_STYLES}</style>
      <div className="bam-modal">

        {/* Toast */}
        {toast && (
          <div className={`bam-toast bam-toast--${toast.type}`}>
            <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
            <span>{toast.message}</span>
          </div>
        )}

        {/* Header */}
        <div className="bam-header">
          <div className="bam-header-icon"><FontAwesomeIcon icon={faUniversity} /></div>
          <div className="bam-header-text">
            <h2>Bank Accounts</h2>
            <p>Manage payout accounts for your withdrawals</p>
          </div>
          <button onClick={onClose} className="bam-close" aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="bam-body">
          {loading ? (
            <div className="bam-loading">
              <FontAwesomeIcon icon={faSpinner} spin />
              <p>Loading bank accounts…</p>
            </div>
          ) : (
            <>
              {/* Add button / form */}
              {!showAddForm ? (
                <button className="bam-add-btn" onClick={() => setShowAddForm(true)} disabled={processing}>
                  <FontAwesomeIcon icon={faPlus} /> Add Bank Account
                </button>
              ) : (
                <form onSubmit={handleAddBank} className="bam-form">
                  <div className="bam-form-title">
                    <FontAwesomeIcon icon={faShieldHalved} />
                    <span>New payout account</span>
                  </div>

                  <p className="bam-note">
                    The account name must match the <strong>first and last name</strong> on your
                    AutoboyExpress profile.
                  </p>

                  {addBankError && (
                    <div className="bam-error-banner">
                      <div className="bam-error-title">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        {addBankError.type === 'name_mismatch' ? 'Account name mismatch'
                          : addBankError.type === 'duplicate' ? 'Already added'
                          : 'Could not add account'}
                      </div>
                      {addBankError.type === 'name_mismatch' ? (
                        <div className="bam-error-body">
                          <div className="bam-name-compare">
                            <div><span>Bank account name</span><strong>{addBankError.bankName}</strong></div>
                            {addBankError.profileName && (
                              <div><span>Your profile name</span><strong>{addBankError.profileName}</strong></div>
                            )}
                          </div>
                          <p>Update your profile name to match your bank account exactly, then try again.</p>
                        </div>
                      ) : (
                        <p className="bam-error-body">{addBankError.message}</p>
                      )}
                      <button type="button" className="bam-error-dismiss" onClick={() => setAddBankError(null)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}

                  {/* Bank picker — searchable, no bank codes exposed */}
                  <div className="bam-field" ref={bankPickerRef}>
                    <label>Bank</label>
                    <div className={`bam-bank-picker ${errors.bank ? 'bam-invalid' : ''}`}>
                      <FontAwesomeIcon icon={faMagnifyingGlass} className="bam-input-icon" />
                      <input
                        type="text"
                        value={bankSearch}
                        placeholder="Search your bank…"
                        onFocus={() => setBankListOpen(true)}
                        onChange={(e) => {
                          setBankSearch(e.target.value);
                          setBankListOpen(true);
                          if (formData.bank_code) {
                            setFormData(prev => ({ ...prev, bank_name: '', bank_code: '', account_name: manualEntry ? prev.account_name : '' }));
                            setVerified(false);
                            setVerifyFailed(false);
                          }
                        }}
                      />
                      <FontAwesomeIcon icon={faChevronDown} className={`bam-chevron ${bankListOpen ? 'open' : ''}`} />
                      {bankListOpen && (
                        <div className="bam-bank-list">
                          {filteredBanks.length === 0 ? (
                            <div className="bam-bank-empty">No bank matches “{bankSearch}”</div>
                          ) : (
                            filteredBanks.map((bank) => (
                              <button
                                type="button"
                                key={`${bank.code}-${bank.name}`}
                                className={`bam-bank-option ${formData.bank_code === bank.code ? 'selected' : ''}`}
                                onClick={() => selectBank(bank)}
                              >
                                <span className="bam-bank-bullet"><FontAwesomeIcon icon={faUniversity} /></span>
                                {bank.name}
                                {formData.bank_code === bank.code && <FontAwesomeIcon icon={faCheckCircle} className="bam-bank-check" />}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {errors.bank && <span className="bam-field-error">{errors.bank}</span>}
                  </div>

                  {/* Account number */}
                  <div className="bam-field">
                    <label>Account number</label>
                    <div className={`bam-input-wrap ${errors.account_number ? 'bam-invalid' : ''}`}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formData.account_number}
                        onChange={(e) => handleAccountNumberChange(e.target.value)}
                        placeholder="10-digit account number"
                      />
                      {verifying && <FontAwesomeIcon icon={faSpinner} spin className="bam-input-status" />}
                      {verified && !verifying && <FontAwesomeIcon icon={faCheckCircle} className="bam-input-status bam-ok" />}
                    </div>
                    {errors.account_number && <span className="bam-field-error">{errors.account_number}</span>}
                  </div>

                  {/* Verification result / account name */}
                  {verifying && (
                    <div className="bam-verify-status bam-verify-loading">
                      <FontAwesomeIcon icon={faSpinner} spin /> Verifying account with your bank…
                    </div>
                  )}

                  {verified && !manualEntry && (
                    <div className="bam-verified-card">
                      <div className="bam-verified-check"><FontAwesomeIcon icon={faCheckCircle} /></div>
                      <div>
                        <span className="bam-verified-label">Account verified</span>
                        <span className="bam-verified-name">{formData.account_name}</span>
                      </div>
                    </div>
                  )}

                  {verifyFailed && !verified && !manualEntry && (
                    <div className="bam-verify-status bam-verify-failed">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <span>
                        We couldn’t verify this account. Check the bank and account number —
                        or{' '}
                        <button type="button" className="bam-link" onClick={() => { setManualEntry(true); setVerifyFailed(false); }}>
                          enter the details manually
                        </button>.
                      </span>
                    </div>
                  )}

                  {manualEntry && (
                    <div className="bam-field">
                      <label>
                        Account name <span className="bam-manual-tag"><FontAwesomeIcon icon={faPen} /> manual</span>
                      </label>
                      <div className={`bam-input-wrap ${errors.account_name ? 'bam-invalid' : ''}`}>
                        <input
                          type="text"
                          value={formData.account_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, account_name: e.target.value }))}
                          placeholder="Exactly as it appears on your bank statement"
                        />
                      </div>
                      <span className="bam-manual-warning">
                        Unverified details can cause withdrawal failures — double-check everything.
                      </span>
                    </div>
                  )}
                  {!manualEntry && errors.account_name && (
                    <span className="bam-field-error">{errors.account_name}</span>
                  )}

                  <div className="bam-form-actions">
                    <button
                      type="button"
                      className="bam-btn-ghost"
                      disabled={processing}
                      onClick={() => { resetForm(); setShowAddForm(false); }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bam-btn-primary"
                      disabled={processing || verifying || (!verified && !manualEntry)}
                    >
                      {processing
                        ? (<><FontAwesomeIcon icon={faSpinner} spin /> Adding…</>)
                        : (<><FontAwesomeIcon icon={faPlus} /> Add Account</>)}
                    </button>
                  </div>
                </form>
              )}

              {/* Accounts list */}
              <div className="bam-list">
                {bankAccounts.length === 0 && !showAddForm ? (
                  <div className="bam-empty">
                    <div className="bam-empty-icon"><FontAwesomeIcon icon={faUniversity} /></div>
                    <h3>No bank accounts yet</h3>
                    <p>Add a bank account to enable wallet withdrawals</p>
                  </div>
                ) : (
                  bankAccounts.map((account) => {
                    const id = account.id || account._id;
                    const isDefault = id === defaultBankId;
                    return (
                      <div key={id} className={`bam-card ${isDefault ? 'bam-card--default' : ''}`}>
                        <div className="bam-card-icon"><FontAwesomeIcon icon={faUniversity} /></div>
                        <div className="bam-card-info">
                          <div className="bam-card-top">
                            <h4>{account.bank_name}</h4>
                            {account.is_verified && (
                              <span className="bam-badge-verified"><FontAwesomeIcon icon={faCheckCircle} /> Verified</span>
                            )}
                            {isDefault && (
                              <span className="bam-badge-default"><FontAwesomeIcon icon={faStar} /> Default</span>
                            )}
                          </div>
                          <p className="bam-card-number">{account.account_number}</p>
                          <p className="bam-card-name">{account.account_name}</p>
                        </div>
                        <div className="bam-card-actions">
                          {!isDefault && (
                            <button className="bam-btn-mini" onClick={() => handleSetDefault(id)} disabled={processing} title="Set as default">
                              <FontAwesomeIcon icon={faStar} />
                            </button>
                          )}
                          <button className="bam-btn-mini bam-btn-danger" onClick={() => handleDeleteBank(id)} disabled={processing} title="Remove account">
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="bam-footnote">
                <FontAwesomeIcon icon={faShieldHalved} />
                <p>Your <strong>default</strong> account receives all wallet withdrawals. Details are verified with your bank before adding.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const BAM_STYLES = `
.bam-overlay {
  position: fixed; inset: 0; z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10, 14, 12, 0.55);
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  padding: 1rem;
}
.bam-modal {
  position: relative;
  width: 100%; max-width: 520px; max-height: 90vh;
  display: flex; flex-direction: column;
  border-radius: 24px; overflow: hidden;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(34, 197, 94, 0.25);
  box-shadow: 0 24px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.4) inset;
  backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px);
  font-family: 'Urbanist', sans-serif;
  color: #111827;
  animation: bamPop 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.2);
}
@keyframes bamPop { from { opacity: 0; transform: translateY(24px) scale(0.97); } to { opacity: 1; transform: none; } }
body.dark-mode .bam-modal { background: rgba(17, 24, 22, 0.92); color: #f3f4f6; border-color: rgba(34,197,94,0.3); }

.bam-toast {
  position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
  z-index: 5; display: flex; align-items: center; gap: 0.5rem;
  padding: 0.65rem 1.1rem; border-radius: 50px;
  font-size: 0.85rem; font-weight: 700; white-space: nowrap;
  animation: bamToastIn 0.3s ease; box-shadow: 0 8px 24px rgba(0,0,0,0.18);
}
@keyframes bamToastIn { from { opacity: 0; transform: translate(-50%, -12px); } to { opacity: 1; transform: translate(-50%, 0); } }
.bam-toast--success { background: linear-gradient(135deg, #22C55E, #16A34A); color: #fff; }
.bam-toast--error { background: linear-gradient(135deg, #ef4444, #dc2626); color: #fff; }

.bam-header {
  display: flex; align-items: center; gap: 1rem;
  padding: 1.5rem 1.75rem 1.25rem;
  background: linear-gradient(135deg, rgba(34,197,94,0.12), rgba(22,163,74,0.05));
  border-bottom: 1px solid rgba(34,197,94,0.15);
}
.bam-header-icon {
  width: 48px; height: 48px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  border-radius: 14px; font-size: 1.25rem; color: #fff;
  background: linear-gradient(135deg, #22C55E, #16A34A);
  box-shadow: 0 8px 20px rgba(34,197,94,0.35);
}
.bam-header-text h2 { margin: 0; font-size: 1.25rem; font-weight: 800; }
.bam-header-text p { margin: 0.15rem 0 0; font-size: 0.82rem; opacity: 0.65; }
.bam-close {
  margin-left: auto; width: 38px; height: 38px; flex-shrink: 0;
  border-radius: 12px; border: 1px solid rgba(120,120,120,0.2);
  background: transparent; color: inherit; cursor: pointer;
  transition: all .2s ease; font-size: 0.95rem;
}
.bam-close:hover { background: rgba(239,68,68,0.12); color: #ef4444; border-color: rgba(239,68,68,0.3); }

.bam-body { padding: 1.25rem 1.75rem 1.5rem; overflow-y: auto; }
.bam-loading { text-align: center; padding: 3rem 0; font-size: 1rem; opacity: 0.7; }
.bam-loading svg { font-size: 2rem; color: #22C55E; margin-bottom: 0.75rem; }

.bam-add-btn {
  width: 100%; padding: 0.95rem; margin-bottom: 1.25rem;
  border: 1.5px dashed rgba(34,197,94,0.5); border-radius: 16px;
  background: rgba(34,197,94,0.06); color: #16A34A;
  font-weight: 700; font-size: 0.95rem; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  transition: all .2s ease; font-family: inherit;
}
.bam-add-btn:hover { background: rgba(34,197,94,0.12); border-style: solid; transform: translateY(-1px); }
body.dark-mode .bam-add-btn { color: #4ade80; }

.bam-form {
  padding: 1.25rem; margin-bottom: 1.25rem;
  border-radius: 18px; border: 1px solid rgba(34,197,94,0.2);
  background: rgba(34,197,94,0.04);
}
.bam-form-title { display: flex; align-items: center; gap: 0.5rem; font-weight: 800; font-size: 1rem; margin-bottom: 0.6rem; color: #16A34A; }
body.dark-mode .bam-form-title { color: #4ade80; }
.bam-note { font-size: 0.8rem; opacity: 0.75; margin: 0 0 1rem; line-height: 1.45; }

.bam-field { margin-bottom: 1rem; position: relative; }
.bam-field label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.8rem; font-weight: 700; margin-bottom: 0.4rem; opacity: 0.85; }

.bam-input-wrap, .bam-bank-picker {
  position: relative; display: flex; align-items: center;
  border: 1.5px solid rgba(120,120,120,0.25); border-radius: 12px;
  background: rgba(255,255,255,0.7); transition: border .2s ease, box-shadow .2s ease;
}
body.dark-mode .bam-input-wrap, body.dark-mode .bam-bank-picker { background: rgba(255,255,255,0.06); }
.bam-input-wrap:focus-within, .bam-bank-picker:focus-within { border-color: #22C55E; box-shadow: 0 0 0 3px rgba(34,197,94,0.15); }
.bam-invalid { border-color: #ef4444 !important; }

.bam-input-wrap input, .bam-bank-picker input {
  flex: 1; padding: 0.8rem 2.4rem 0.8rem 0.9rem; border: none; outline: none;
  background: transparent; color: inherit; font-size: 0.95rem; font-family: inherit; min-width: 0;
}
.bam-bank-picker input { padding-left: 2.3rem; }
.bam-input-icon { position: absolute; left: 0.85rem; opacity: 0.45; font-size: 0.85rem; pointer-events: none; }
.bam-chevron { position: absolute; right: 0.9rem; opacity: 0.5; font-size: 0.8rem; transition: transform .2s ease; pointer-events: none; }
.bam-chevron.open { transform: rotate(180deg); }
.bam-input-status { position: absolute; right: 0.9rem; color: #22C55E; }
.bam-input-status.bam-ok { animation: bamPopIn 0.3s ease; }
@keyframes bamPopIn { from { transform: scale(0); } to { transform: scale(1); } }

.bam-bank-list {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 30;
  max-height: 240px; overflow-y: auto;
  border-radius: 14px; border: 1px solid rgba(34,197,94,0.25);
  background: #ffffff; box-shadow: 0 16px 48px rgba(0,0,0,0.18);
}
body.dark-mode .bam-bank-list { background: #16211c; }
.bam-bank-option {
  display: flex; align-items: center; gap: 0.65rem; width: 100%;
  padding: 0.7rem 0.9rem; border: none; background: transparent;
  color: inherit; font-size: 0.9rem; font-family: inherit; text-align: left; cursor: pointer;
}
.bam-bank-option:hover { background: rgba(34,197,94,0.1); }
.bam-bank-option.selected { background: rgba(34,197,94,0.14); font-weight: 700; }
.bam-bank-bullet { width: 28px; height: 28px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: rgba(34,197,94,0.12); color: #16A34A; font-size: 0.75rem; }
body.dark-mode .bam-bank-bullet { color: #4ade80; }
.bam-bank-check { margin-left: auto; color: #22C55E; }
.bam-bank-empty { padding: 1rem; font-size: 0.85rem; opacity: 0.6; text-align: center; }

.bam-field-error { display: block; margin-top: 0.35rem; color: #ef4444; font-size: 0.78rem; font-weight: 600; }

.bam-verify-status { display: flex; align-items: flex-start; gap: 0.6rem; padding: 0.8rem 1rem; border-radius: 12px; font-size: 0.85rem; margin-bottom: 1rem; line-height: 1.45; }
.bam-verify-loading { background: rgba(59,130,246,0.1); color: #3b82f6; }
.bam-verify-failed { background: rgba(245,158,11,0.12); color: #b45309; }
body.dark-mode .bam-verify-failed { color: #fbbf24; }
.bam-link { border: none; background: none; padding: 0; color: #16A34A; font-weight: 700; cursor: pointer; text-decoration: underline; font-size: inherit; font-family: inherit; }
body.dark-mode .bam-link { color: #4ade80; }

.bam-verified-card {
  display: flex; align-items: center; gap: 0.85rem;
  padding: 0.9rem 1rem; margin-bottom: 1rem;
  border-radius: 14px; border: 1px solid rgba(34,197,94,0.35);
  background: linear-gradient(135deg, rgba(34,197,94,0.12), rgba(22,163,74,0.06));
  animation: bamPop 0.35s ease;
}
.bam-verified-check { width: 38px; height: 38px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: linear-gradient(135deg, #22C55E, #16A34A); color: #fff; font-size: 1rem; box-shadow: 0 6px 16px rgba(34,197,94,0.4); }
.bam-verified-label { display: block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #16A34A; }
body.dark-mode .bam-verified-label { color: #4ade80; }
.bam-verified-name { display: block; font-size: 1rem; font-weight: 800; margin-top: 0.1rem; }

.bam-manual-tag { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.15rem 0.55rem; border-radius: 50px; background: rgba(245,158,11,0.15); color: #b45309; font-size: 0.68rem; font-weight: 700; }
body.dark-mode .bam-manual-tag { color: #fbbf24; }
.bam-manual-warning { display: block; margin-top: 0.35rem; font-size: 0.75rem; color: #b45309; }
body.dark-mode .bam-manual-warning { color: #fbbf24; }

.bam-error-banner {
  position: relative; padding: 0.9rem 2.4rem 0.9rem 1rem; margin-bottom: 1rem;
  border-radius: 14px; border: 1px solid rgba(239,68,68,0.3); background: rgba(239,68,68,0.08);
  font-size: 0.85rem;
}
.bam-error-title { display: flex; align-items: center; gap: 0.5rem; font-weight: 800; color: #dc2626; margin-bottom: 0.35rem; }
body.dark-mode .bam-error-title { color: #f87171; }
.bam-error-body { margin: 0; line-height: 1.5; }
.bam-error-body p { margin: 0.4rem 0 0; }
.bam-name-compare { display: grid; gap: 0.3rem; padding: 0.6rem 0.75rem; margin: 0.4rem 0; border-radius: 10px; background: rgba(0,0,0,0.05); }
body.dark-mode .bam-name-compare { background: rgba(255,255,255,0.06); }
.bam-name-compare span { display: inline-block; min-width: 140px; opacity: 0.65; font-size: 0.78rem; }
.bam-error-dismiss { position: absolute; top: 0.7rem; right: 0.7rem; border: none; background: none; color: inherit; opacity: 0.5; cursor: pointer; }
.bam-error-dismiss:hover { opacity: 1; }

.bam-form-actions { display: grid; grid-template-columns: 1fr 1.4fr; gap: 0.75rem; margin-top: 1.1rem; }
.bam-btn-ghost, .bam-btn-primary {
  padding: 0.85rem; border-radius: 12px; font-size: 0.92rem; font-weight: 700;
  font-family: inherit; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  transition: all .2s ease;
}
.bam-btn-ghost { border: 1.5px solid rgba(120,120,120,0.3); background: transparent; color: inherit; }
.bam-btn-ghost:hover { border-color: rgba(120,120,120,0.6); }
.bam-btn-primary { border: none; color: #fff; background: linear-gradient(135deg, #22C55E, #16A34A); box-shadow: 0 8px 20px rgba(34,197,94,0.35); }
.bam-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 26px rgba(34,197,94,0.45); }
.bam-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }

.bam-list { display: grid; gap: 0.85rem; }
.bam-empty { text-align: center; padding: 2.5rem 1rem; }
.bam-empty-icon { width: 64px; height: 64px; margin: 0 auto 1rem; display: flex; align-items: center; justify-content: center; border-radius: 20px; background: rgba(34,197,94,0.1); color: #22C55E; font-size: 1.5rem; }
.bam-empty h3 { margin: 0 0 0.3rem; font-size: 1.05rem; }
.bam-empty p { margin: 0; font-size: 0.85rem; opacity: 0.6; }

.bam-card {
  display: flex; align-items: center; gap: 1rem; padding: 1rem 1.1rem;
  border-radius: 16px; border: 1px solid rgba(120,120,120,0.18);
  background: rgba(255,255,255,0.55); transition: all .2s ease;
}
body.dark-mode .bam-card { background: rgba(255,255,255,0.05); }
.bam-card--default { border-color: rgba(34,197,94,0.45); background: rgba(34,197,94,0.07); }
.bam-card-icon { width: 44px; height: 44px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 12px; background: rgba(34,197,94,0.12); color: #16A34A; font-size: 1.05rem; }
body.dark-mode .bam-card-icon { color: #4ade80; }
.bam-card-info { flex: 1; min-width: 0; }
.bam-card-top { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.bam-card-top h4 { margin: 0; font-size: 0.95rem; font-weight: 800; }
.bam-card-number { margin: 0.2rem 0 0; font-size: 0.88rem; letter-spacing: 0.08em; font-weight: 600; opacity: 0.85; }
.bam-card-name { margin: 0.1rem 0 0; font-size: 0.8rem; opacity: 0.6; }
.bam-badge-verified, .bam-badge-default {
  display: inline-flex; align-items: center; gap: 0.3rem;
  padding: 0.15rem 0.55rem; border-radius: 50px; font-size: 0.66rem; font-weight: 800; letter-spacing: 0.04em;
}
.bam-badge-verified { background: rgba(34,197,94,0.15); color: #16A34A; }
body.dark-mode .bam-badge-verified { color: #4ade80; }
.bam-badge-default { background: rgba(245,158,11,0.15); color: #b45309; }
body.dark-mode .bam-badge-default { color: #fbbf24; }
.bam-card-actions { display: flex; gap: 0.5rem; flex-shrink: 0; }
.bam-btn-mini {
  width: 36px; height: 36px; border-radius: 10px; cursor: pointer;
  border: 1px solid rgba(120,120,120,0.25); background: transparent; color: inherit;
  transition: all .2s ease; font-size: 0.85rem;
}
.bam-btn-mini:hover { border-color: #22C55E; color: #16A34A; background: rgba(34,197,94,0.08); }
.bam-btn-danger:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,0.08); }

.bam-footnote { display: flex; gap: 0.6rem; align-items: flex-start; margin-top: 1.25rem; padding: 0.85rem 1rem; border-radius: 12px; background: rgba(120,120,120,0.07); font-size: 0.78rem; line-height: 1.5; opacity: 0.85; }
.bam-footnote svg { margin-top: 0.15rem; color: #22C55E; flex-shrink: 0; }
.bam-footnote p { margin: 0; }

@media (max-width: 560px) {
  .bam-overlay { padding: 0; align-items: flex-end; }
  .bam-modal { max-width: 100%; max-height: 94vh; border-radius: 24px 24px 0 0; }
  .bam-form-actions { grid-template-columns: 1fr 1fr; }
}
`;

export default BankAccountsManager;
